import { Navigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

interface CustomJwtPayload {
  guestId?: string;
  exp?: number;
  // add other fields if needed
}

export const protectGuestPortal = (navigate: (path: string) => void) => {
  const authToken = localStorage.getItem("authToken");
  if (!authToken) {
    navigate("/guests/login");
    return false;
  }

  try {
    const decoded = jwtDecode<CustomJwtPayload>(authToken);

    if (!decoded?.exp || !decoded.guestId) {
      navigate("/guests/login");
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    if (decoded.exp <= now) {
      navigate("/guests/login");
      return false;
    }

    return true; // token is valid
  } catch (err) {
    console.error("Invalid token:", err);
    navigate("/guests/login");
    return false;
  }
};


/**
 * Checks if a user has permission to access a specific feature.
 * Opens a modal if access is restricted.
 *
 * @param {"sales" | "bookings" | "orders" | "rooms" | "forceDeleteBooking"} feature - The feature to check.
 * @param {Record<string, boolean>} access - User access permissions.
 * @param {Function} openModal - Function to show the modal.
 * @param {Function} closeModal - Function to close the modal.
 * @param {string} [customMessage] - Optional custom restriction message.
 * @returns {boolean} True if access is granted, otherwise false.
 */

export function checkAccess(
  feature:
    "sales" | "bookings" | "orders" | "rooms" | "loanActionAccess"
    | "forceDeleteBooking" | "updateBooking" | "forceDeleteOrder" 
    | "leaveActionAccess" | "deleteGuest"| "updateGuest" | "notifyGuest"
    | "deleteStaffRequest",
  access: Record<string, boolean>,
  openModal: (
    title: string,
    message: string,
    closeFn: () => void,
    showCancel?: boolean,
    confirmText?: string
  ) => void,
  closeModal: () => void,
  customMessage?: string
): boolean {
  const accessMap: Record<string, string> = {
    sales: "You don’t have permission to view daily sales information.",
    bookings: "You don’t have permission to view or manage room or guest bookings.",
    orders: "You don’t have permission to view or manage guest orders.",
    rooms: "You don’t have permission to view or manage room information.",
    forceDeleteBooking: "You don’t have permission to delete bookings.",
    forceDeleteOrder: "You don’t have permission to orders.",
    deleteGuest: "You don’t have permission to delete guest.",
    updateGuest: "You don’t have permission to update guest details.",
    updateBooking: "You don’t have permission to update bookings.",
    notifyGuest: "You don’t have permission to send notification to guest.",
    loanActionAccess: "You don’t have permission to approve or reject loan requests.",
    leaveActionAccess: "You don’t have permission to approve or reject leave requests.",
    deleteStaffRequest: "You don’t have permission to delete leave or loan requests.",
  };

  if (!access[feature]) {
    openModal(
      "Access Restricted 🔒",
      `${customMessage || accessMap[feature]}
--------------------------------------------
Please contact your admin if you believe you should have access to this feature.`,
      closeModal,
      false,
      "Close"
    );
    return false;
  }

  return true;
}

/**
 * Computes user access permissions for bookings or orders.
 * Reads from localStorage and determines available actions.
 *
 * @param {"orders" | "bookings"} type - The type of access to compute.
 * @returns An object containing computed access flags based on type.
 */
export function computeBookingOrderAccess<T extends "orders" | "bookings">(type: T):
  T extends "bookings"
  ? { forceDeleteBooking: boolean; updateBooking: boolean }
  : { forceDeleteOrder: boolean } {

  if (type === "bookings") {
    return computeAccess(
      "bookings",
      {
        bookings_delete_paid: "forceDeleteBooking",
        bookings_update: "updateBooking",
      },
      { forceDeleteBooking: false, updateBooking: false }
    ) as any;
  }

  return computeAccess(
    "orders",
    { orders_delete_paid: "forceDeleteOrder" },
    { forceDeleteOrder: false }
  ) as any;
}


type PermissionAction = Record<string, boolean>;

// General utility to compute access from user permissions
export function computeAccess<T extends PermissionAction>(
  type: string,
  permissionMap: Record<string, keyof T>,
  defaultActions: T
): T {
  try {
    const userStr = localStorage.getItem("user");
    const selectedAction = { ...defaultActions };

    if (!userStr) return selectedAction;

    const user = JSON.parse(userStr);

    if (!user?.permissions?.length) return selectedAction;

    user.permissions.forEach((perm: any) => {
      if (perm.type === "action" && perm.value === true) {
        const mappedKey = permissionMap[perm.name];
        if (mappedKey && mappedKey in selectedAction) {
          (selectedAction as any)[mappedKey] = true;
        }
      }
    });

    return selectedAction;
  } catch (error) {
    console.error(`Failed to compute access for ${type}:`, error);
    return defaultActions;
  }
}



export function computeGuestActionAccess<
  T extends "deleteGuest" | "updateGuest"
>(
  type: T
): T extends "deleteGuest"
  ? { deleteGuest: boolean }
  : { updateGuest: boolean } {

  if (type === "deleteGuest") {
    return computeAccess(
      "deleteGuest",
      { guests_delete: "deleteGuest" },
      { deleteGuest: false }
    ) as any;
  }

  return computeAccess(
    "updateGuest",
    { guests_update: "updateGuest" },
    { updateGuest: false }
  ) as any;
}


export function computeStaffRequestActionAccess<T extends "loan" | "leave">(type: T):
  T extends "loan"
  ? { loanActionAccess: boolean }
  : { leaveActionAccess: boolean } {

  if (type === "loan") {
    return computeAccess(
      "loan",
      { loans_approve_reject: "loanActionAccess" },
      { loanActionAccess: false }
    ) as any;
  }

  return computeAccess(
    "leave",
    { leaves_approve_reject: "leaveActionAccess" }, // fixed wrong key name
    { leaveActionAccess: false }
  ) as any;
}